import { ReactNode } from 'react';
import type { ServiceEntry } from '../services/types';
import { ServiceSwitcher } from './ServiceSwitcher';
import { MenuIcon } from '../icons';

interface TopBarProps {
  /** Current subsystem display name, shown as the dropdown label. */
  appName: string;
  /** URL for the home/portal link on the logo. Default "/". */
  homeUrl?: string;
  /** All services for the dropdown switcher. */
  services: ServiceEntry[];
  /** ID of the currently active service (from findActiveServiceId). */
  activeServiceId?: string | null;
  /** Trailing breadcrumb segment — typically the active sidebar page label. */
  currentPageLabel?: string | null;
  /** Right-side slot for page-scoped actions (Filter / Export / New / ...). */
  actions?: ReactNode;
  /** Called when the mobile hamburger button is clicked. */
  onToggleDrawer: () => void;
  /** Optional custom logo node. Default: a circular mark with "B". */
  logo?: ReactNode;
}

export function TopBar({
  appName,
  homeUrl = '/',
  services,
  activeServiceId,
  currentPageLabel,
  actions,
  onToggleDrawer,
  logo,
}: TopBarProps) {
  return (
    <header className="sticky top-0 z-40 h-14 border-b bg-background/90 backdrop-blur flex items-center px-2 sm:px-3 gap-2 sm:gap-3">
      {/* Mobile: hamburger for sidebar drawer.
          Bigger touch target (44x44) on phone, plus extra spacing to the
          right so users don't mis-tap the adjacent service-switcher dropdown. */}
      <button
        type="button"
        onClick={onToggleDrawer}
        aria-label="Open menu"
        className="lg:hidden inline-flex items-center justify-center w-11 h-11 lg:w-9 lg:h-9 mr-1 sm:mr-2 lg:mr-0 rounded-md text-foreground hover:bg-muted active:bg-muted/70"
      >
        <MenuIcon className="w-6 h-6 lg:w-5 lg:h-5" />
      </button>

      {/* Left cluster: logo · service switcher · breadcrumb */}
      <nav aria-label="Breadcrumb" className="flex items-center gap-2 min-w-0">
        <a
          href={homeUrl}
          aria-label="Go to portal home"
          className="flex items-center gap-2 shrink-0"
        >
          {logo ?? <DefaultLogo />}
          <span className="hidden sm:inline text-sm font-semibold text-foreground">
            Ops Hub
          </span>
        </a>

        <Sep />

        <ServiceSwitcher
          services={services}
          activeServiceId={activeServiceId}
          currentLabel={appName}
        />

        {currentPageLabel && (
          <>
            <Sep className="hidden md:inline" />
            <span className="hidden md:inline text-sm text-foreground/80 truncate max-w-[14rem]">
              {currentPageLabel}
            </span>
          </>
        )}
      </nav>

      {/* Right cluster: page-scoped actions only */}
      {actions && <div className="ml-auto flex items-center gap-2">{actions}</div>}
      {!actions && <div className="ml-auto" />}
    </header>
  );
}

function Sep({ className = '' }: { className?: string }) {
  return (
    <span
      aria-hidden
      className={
        'text-muted-foreground/40 text-sm select-none hidden sm:inline ' + className
      }
    >
      /
    </span>
  );
}

function DefaultLogo() {
  return (
    <span
      aria-hidden
      className="inline-flex items-center justify-center w-7 h-7 rounded-md bg-gradient-to-br from-sky-400/90 to-indigo-500/90 dark:from-sky-500/80 dark:to-indigo-600/80 text-white"
    >
      <svg
        viewBox="0 0 32 32"
        className="w-3.5 h-3.5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <rect x="5" y="5" width="9" height="9" rx="1.5" />
        <rect x="18" y="5" width="9" height="9" rx="1.5" />
        <rect x="5" y="18" width="9" height="9" rx="1.5" />
        <rect x="18" y="18" width="9" height="9" rx="1.5" />
      </svg>
    </span>
  );
}
